import { useState, useEffect, forwardRef, useRef, memo, ForwardedRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import { isFunction } from "lodash";
import { FixedSizeList, areEqual } from "react-window";
import { BOLD_TEXT, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { UseOptionsProps, useModal } from "@/comn/hooks";
import { utils, comnUtils, comnEnvs } from "@/comn/utils";
import { Icon } from "@/comn/components";
import { COMN_CODE } from "@/comn/constants";
import { useOptions_v2 } from "@/comn/hooks/useOptions.v2";

type InputCodeProps = UseOptionsProps & {
    exact?: boolean;
    edit?: boolean;
    mode?: "edit" | "view" | null;
    popupSize?: "sm" | "md" | "lg";
    popupParams?: any;
    searchParams?: any;
    viewType?: "label" | "value" | "both";

    value?: any;
    name?: string;
    placeholder?: string;
    readOnly?: boolean;
    readOnlyInput?: boolean;
    disabled?: boolean;
    maxLength?: number;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    disableLabel?: boolean;
    disableSearch?: boolean;

    callback?: (arg?: any) => void;
    onBlur?: (arg?: any) => void;
    onFocus?: (arg?: any) => void;
    onChange?: (arg?: any) => void;

    onBeforeClickSearch?: (arg?: any) => boolean;

    async?: any;
};

export const InputCode2 = forwardRef((props: InputCodeProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
        edit = true,
        mode,
        popupParams,
        disableLabel,
        disableSearch,
        viewType = "both",
        exact = true,
        readOnlyInput,

        /** */
        value,
        name,
        readOnly,
        disabled,
        maxLength,
        placeholder,
        onBlur,
        onFocus,
        onChange,
        callback,
        onBeforeClickSearch,

        // For style
        color,
        bold,
        editColor,
        borderColor,
        editBold,
        fontSize,
        // For useOptions
        options: propsOptions,
        area,
        asyncParams: params,
        comnCd,
        excludes,
        includes,
        filter,
        async = area && COMN_CODE[area]?.async,
    } = props;

    const _props = Object.fromEntries(
        Object.entries({
            name,
            readOnly,
            disabled,
            maxLength,
            placeholder,
            onFocus,
        }).filter(([, value]) => value !== undefined),
    );

    const $ = useRef<any>({
        containerRef: null,
        inputRef: null,
        options: [],
        count: 0,
        isOpen: false,
    });
    const { openModal, closeModal } = useModal();
    const [isOpen, setIsOpen] = useState(false);

    const hidden = mode === "view" ? true : mode === "edit" ? false : !edit;

    const { options, optionsItem, isLoading, setOption, getLoading, findDataItem } = useOptions_v2({
        componentType: "code",
        propsOptions,
        area,
        comnCd,
        params,
        value,
        excludes,
        includes,
        filter,
        isOpen,
    });

    const toggleList = (status: any) => {
        $.current.isOpen = status;
        setIsOpen(status);
    };

    const handleCallback = (data: any) => {
        // console.log(data);
        if (callback) {
            callback(data);
        }
    };

    const handleClick = () => {
        if (readOnlyInput || readOnly || disabled) return;
        toggleList(true);
    };

    const handleBlur = (event: any) => {
        // if (exact && onChange && !$.current.isOpen) {
        //     onChange("");
        // }

        if (onBlur) onBlur(event);
    };

    const handleChange = (event: any) => {
        if (getLoading()) return;
        const nextValue = event.target.value;
        if (typeof maxLength === "number" && maxLength < nextValue.length) return;
        if (onChange) onChange(formatCode(nextValue));
        toggleList(true);
    };

    const handleClickSearch = () => {
        if (disabled === true) return;
        const params = { ...(utils.toValues(popupParams) || {}), comnCd };
        if (onBeforeClickSearch && onBeforeClickSearch({ popupParams: params }) === false) return;
        const id = uuid();
        const popupKey = ((Object.keys(comnEnvs.popup).includes(area || "") && area) ||
            "comnCd") as keyof typeof comnEnvs.popup;
        const url = comnEnvs.popup[popupKey];
        openModal({
            id,
            url,
            params,
            size: "sm",
            draggable: true,
            callback: (data: any) => {
                closeModal(id);
                handleCallback(data);
                setOption({ value: data.code, label: data.label });
                if (onChange) onChange(data.code);
            },
        });
    };

    $.current.area = area;
    $.current.comnCd = comnCd;
    $.current.exact = exact;
    $.current.options = options;
    $.current.count = options.length;
    $.current.onChange = onChange;
    $.current.toggleList = toggleList;
    $.current.setOption = setOption;
    $.current.handleCallback = handleCallback;
    $.current.findDataItem = findDataItem;

    return (
        <>
            <div
                ref={(element) => {
                    if (element) {
                        $.current.containerRef = element;
                    }
                }}
                className="w-full"
                data-async={async}
                data-status={isLoading}
            >
                {hidden && (
                    <div
                        title={viewCode(value, { options, viewType, optionsItem }, true)}
                        className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}
                    >
                        {viewCode(value, { options, viewType, optionsItem })}
                    </div>
                )}

                <div hidden={hidden}>
                    <div className="w-full flex relative">
                        <input
                            {..._props}
                            ref={(element) => {
                                if (element) {
                                    $.current.inputRef = element;
                                    if (isFunction(ref)) {
                                        ref(element);
                                    } else if (ref?.current) {
                                        ref.current = element;
                                    }
                                }
                            }}
                            readOnly={readOnly || readOnlyInput || disabled}
                            value={formatCode(value)}
                            onBlur={handleBlur}
                            onClick={handleClick}
                            onChange={handleChange}
                            autoComplete="off"
                            className={classNames(
                                "input flex-1",
                                comnUtils.getEditStyle(editColor, editBold),
                                (!disableSearch || !disableLabel) && "rounded-r-none",
                                borderColor ? COLOR_BORDER[borderColor] : "",
                            )}
                        />
                        {!disableSearch && (
                            <button
                                type="button"
                                className={classNames(
                                    "button border-l-0 rounded-l-none",
                                    !disableLabel && "rounded-none border-x-0",
                                )}
                                onClick={handleClickSearch}
                                disabled={readOnly || disabled}
                            >
                                <Icon icon="search" size="xs" />
                            </button>
                        )}
                        {!disableLabel && (
                            <input
                                readOnly={true}
                                disabled={disabled}
                                value={optionsItem?.label || ""}
                                title={optionsItem?.label || ""}
                                className={classNames(
                                    "input rounded-l-none flex-[2]",
                                    comnUtils.getEditStyle(editColor, editBold),
                                )}
                            />
                        )}
                    </div>
                </div>

                {isLoading && (
                    <div className="absolute flex items-center justify-end px-1.5 w-full h-full top-0 left-0 rounded bg-black/5">
                        <Icon icon="loading" size="xs" className="animate-spin text-primary" />
                    </div>
                )}
            </div>

            {isOpen && <List $InputCode={$} options={options} isLoading={isLoading} />}
        </>
    );
});

const List = (props: any) => {
    const { $InputCode, options, isLoading } = props;

    const $ = useRef<any>({ listRef: null, cursor: null });
    const [cursor, setCursor] = useState<any>(null);

    const handleChange = (option: any) => {
        const onChange = $InputCode.current.onChange;
        if (onChange) {
            onChange(option.value);
            $InputCode.current.setOption(option);
        }
        $InputCode.current.handleCallback({
            code: option.value,
            label: option.label,
            data: $InputCode.current.findDataItem(option.value),
        });
        $.current.cursor = null;
        setCursor(null);
        $InputCode.current.toggleList(false);
    };

    const handleClose = (event?: any) => {
        if (event) {
            if (event.type === "click") {
                if (event.target === $InputCode.current.inputRef) return;
            }
        }

        $.current.cursor = null;
        setCursor(null);
        $InputCode.current.toggleList(false);
    };

    useEffect(() => {
        return () => {
            // console.log("close");
        };
    }, []);

    useEffect(() => {
        if ($.current.listRef && cursor !== null) {
            $.current.listRef.scrollToItem(cursor);
        }
    }, [cursor]);

    useEffect(() => {
        const handleKeydown = (event: any) => {
            switch (event.keyCode) {
                case 9: // tab
                case 27: // esc
                    handleClose();
                    break;
                case 38: // up
                    setCursor((prev: any) => {
                        const next = prev === null || prev - 1 < 0 ? 0 : prev - 1;
                        $.current.cursor = next;
                        return next;
                    });
                    break;
                case 40: // down
                    setCursor((prev: any) => {
                        const next = prev === null ? 0 : prev + 1 > $InputCode.current.count - 1 ? prev : prev + 1;
                        $.current.cursor = next;
                        return next;
                    });
                    break;
                case 13: // enter
                    const cursor = $.current.cursor;
                    if (cursor === null) return;
                    const option = $InputCode.current.options[cursor];
                    if (option) handleChange(option);
                    break;
            }
        };

        window.addEventListener("keydown", handleKeydown);
        window.addEventListener("wheel", handleClose);
        window.addEventListener("scroll", handleClose);
        window.addEventListener("resize", handleClose);
        window.addEventListener("click", handleClose);

        return () => {
            window.removeEventListener("keydown", handleKeydown);
            window.removeEventListener("wheel", handleClose);
            window.removeEventListener("scroll", handleClose);
            window.removeEventListener("resize", handleClose);
            window.removeEventListener("click", handleClose);
        };
    }, []);

    const { width, top, height, left } = $InputCode.current.containerRef.getBoundingClientRect();

    return createPortal(
        <div
            onClick={(event) => {
                event.stopPropagation();
            }}
            className="fixed translate-y-1 rounded bg-background/95 min-w-[12rem] border shadow z-[9999]"
            style={{ width, left, top: top + height }}
        >
            <FixedSizeList
                ref={(list) => {
                    if (list) {
                        $.current.listRef = list;
                    }
                }}
                innerElementType="ul"
                outerRef={(element) => {
                    if (element)
                        element.onwheel = (event: any) => {
                            event.stopPropagation();
                        };
                }}
                itemCount={options.length}
                width="100%"
                height={options.length * 40 > 300 ? 300 : options.length * 40}
                itemSize={40}
                itemData={{ handleChange, options, cursor }}
            >
                {Row}
            </FixedSizeList>
            {isLoading && (
                <div className="absolute flex items-center justify-center w-full h-full top-0 left-0 rounded bg-black/5">
                    <Icon icon="loading" className="animate-spin text-primary" />
                </div>
            )}
        </div>,
        document.body,
    );
};

const Row = memo((props: any) => {
    const { data, index, style } = props;
    const { handleChange, options, cursor } = data;
    const option = options[index];
    const { value, label, type } = option;

    return (
        <li
            title={label}
            onClick={() => type !== "NO_DATA" && handleChange(option)}
            aria-selected={cursor === index}
            className={classNames(
                "flex items-center p-2 not cursor-pointer [&:not(:last-child)]:border-b hover:bg-gray/10 dark:hover:bg-white/10 aria-selected:bg-gray/20",
            )}
            style={style}
        >
            <span className="truncate">{type === "NO_DATA" ? "No data." : `[${value}] ${label}`}</span>
        </li>
    );
}, areEqual);

const viewCode = (v: any, o?: any, all?: boolean) => {
    // if (!o?.options) return;
    // const option = o.options?.find(({ value }: any) => value === v);

    if (!o?.optionsItem) return;
    // if (!option) return;
    const vt = o.optionsItem.value || "";
    const lt = o.optionsItem.label || "";
    const vtWithBracket = o.optionsItem.value ? `[${o.optionsItem.value}] ` : "";

    let view = o?.viewType === "value" ? vt : o?.viewType === "label" ? lt : vtWithBracket + lt;
    if (all === true) {
        view = vtWithBracket + lt;
    }
    return view;
};

const formatCode = (v: any, o?: any) => {
    if (!v) return "";
    let f = String(v);
    return f;
};

const unformatCode = (v: any, o?: any) => {
    if (!v) return undefined;
    let f = String(v);
    return f;
};
